
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

// Enhanced Toast Animation keyframes
const slideInRight = keyframes`
  from {
    transform: translateX(100%) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
`;

const slideOutRight = keyframes`
  from {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  to {
    transform: translateX(100%) scale(0.95);
    opacity: 0;
  }
`;

const progressAnimation = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(102, 126, 234, 0.5), 0 0 40px rgba(102, 126, 234, 0.2);
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
  60% {
    transform: translateY(-2px);
  }
`;

// Enhanced Toast Components
const ToastContainer = styled.div`
  position: fixed;
  top: 2rem;
  right: 2rem;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  pointer-events: none;
  
  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    left: 1rem;
  }
`;

const Toast = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  padding: 1.5rem;
  border-radius: 20px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.15),
    0 8px 32px rgba(102, 126, 234, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 420px;
  min-width: 380px;
  position: relative;
  overflow: hidden;
  pointer-events: auto;
  animation: ${props => props.isVisible ? slideInRight : slideOutRight} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  
  @media (max-width: 768px) {
    min-width: auto;
    max-width: none;
    padding: 1.25rem;
    border-radius: 16px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    animation: ${shimmer} 3s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const ToastHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  position: relative;
  z-index: 2;
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.1));
  border-radius: 12px;
  font-size: 1.2rem;
  animation: ${bounce} 2s ease-in-out infinite;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ToastTitleSection = styled.div`
  flex: 1;
`;

const ToastTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const ToastSubtitle = styled.div`
  font-size: 0.8rem;
  opacity: 0.8;
  color: #e2e8f0;
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 10px;
  transition: all 0.2s ease;
  opacity: 0.8;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ToastMessage = styled.p`
  margin: 0 0 1rem 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.95);
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  width: 100%;
  border-radius: 0 0 20px 20px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  animation: ${progressAnimation} ${props => props.duration}ms linear forwards;
  border-radius: 0 0 20px 0;
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
  position: relative;
  z-index: 2;
`;

const ActionButton = styled.button`
  background: ${props => props.primary ? 
    'linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.15))' : 
    'rgba(255, 255, 255, 0.1)'
  };
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  padding: 0.6rem 1.2rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  box-shadow: ${props => props.primary ? 
    '0 4px 12px rgba(0, 0, 0, 0.1)' : 
    '0 2px 8px rgba(0, 0, 0, 0.05)'
  };
  
  &:hover {
    background: ${props => props.primary ? 
      'linear-gradient(135deg, rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.25))' : 
      'rgba(255, 255, 255, 0.2)'
    };
    transform: translateY(-2px);
    box-shadow: ${props => props.primary ? 
      '0 8px 20px rgba(0, 0, 0, 0.15)' : 
      '0 4px 12px rgba(0, 0, 0, 0.1)'
    };
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const StatusBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #ffffff;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
  animation: ${glow} 2s ease-in-out infinite;
  border: 2px solid rgba(255, 255, 255, 0.2);
`;

// Enhanced Toast Component
const ToastExample = ({ 
  isVisible = true, 
  onClose, 
  autoClose = true, 
  duration = 10000,
  showActions = true 
}) => {
  const [visible, setVisible] = useState(isVisible);

  useEffect(() => {
    if (autoClose && isVisible) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, autoClose, duration]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 400);
  };

  const handleViewProgress = () => {
    console.log('View progress clicked');
    // You can redirect to a progress page or show more details
    handleClose();
  };

  const handleDismiss = () => {
    handleClose();
  };

  return (
    <ToastContainer>
      <Toast isVisible={visible}>
        <StatusBadge>
          In Progress
        </StatusBadge>
        
        <ToastHeader>
          <IconContainer>
            🚧
          </IconContainer>
          <ToastTitleSection>
            <ToastTitle>Design in Progress</ToastTitle>
            <ToastSubtitle>Case Studies Under Development</ToastSubtitle>
          </ToastTitleSection>
          <CloseButton onClick={handleClose}>
            ✕
          </CloseButton>
        </ToastHeader>
        
        <ToastMessage>
          We're crafting detailed case studies with enhanced visuals and comprehensive project insights. 
          Our design team is working on creating an exceptional portfolio experience for you.
        </ToastMessage>
        
        {showActions && (
          <ActionButtons>
            {/* <ActionButton primary onClick={handleViewProgress}>
              📊 View Progress
            </ActionButton> */}
            <ActionButton onClick={handleDismiss}>
              👍 Got it
            </ActionButton>
          </ActionButtons>
        )}
        
        {autoClose && (
          <ProgressBar>
            <ProgressFill duration={duration} />
          </ProgressBar>
        )}
      </Toast>
    </ToastContainer>
  );
};


export default ToastExample;